/**
 * Status pills.
 *
 * Every tone renders as a `-soft` background with its matching `-on-soft`
 * foreground, which is the only pairing guaranteed to hold its contrast in both
 * themes — so a Badge never needs a `dark:` override.
 *
 * `EnumBadge` is the form used almost everywhere in the app: it takes the
 * `EnumDisplay` metadata from @aforium/shared, so a status label and its tone
 * are read from one place instead of being retyped on every screen.
 */
import { forwardRef, type HTMLAttributes } from 'react';
import { cva } from 'class-variance-authority';
import type { BadgeTone, EnumDisplay } from '@aforium/shared';
import { cn } from '../../lib/cn';

const badgeVariants = cva(
  'inline-flex min-w-0 max-w-full items-center gap-1.5 rounded-full border align-middle font-medium',
  {
    variants: {
      tone: {
        neutral: 'border-border-strong/40 bg-neutral-soft text-neutral-on-soft',
        info: 'border-info/20 bg-info-soft text-info-on-soft',
        success: 'border-success/20 bg-success-soft text-success-on-soft',
        warning: 'border-warning/25 bg-warning-soft text-warning-on-soft',
        danger: 'border-danger/20 bg-danger-soft text-danger-on-soft',
        accent: 'border-accent/20 bg-accent-soft text-accent-on-soft',
      },
      size: {
        sm: 'h-5 px-2 text-2xs',
        md: 'h-6 px-2.5 text-xs',
      },
    },
    defaultVariants: {
      tone: 'neutral',
      size: 'md',
    },
  },
);

export interface BadgeProps extends HTMLAttributes<HTMLSpanElement> {
  tone: BadgeTone;
  size?: 'sm' | 'md';
  /** Leading dot, for tone-only distinctions such as live/offline states. */
  dot?: boolean;
}

export const Badge = forwardRef<HTMLSpanElement, BadgeProps>(function Badge(
  { className, tone, size = 'md', dot = false, children, ...props },
  ref,
) {
  return (
    <span ref={ref} className={cn(badgeVariants({ tone, size }), className)} {...props}>
      {dot ? (
        <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-current" aria-hidden="true" />
      ) : null}
      <span className="truncate">{children}</span>
    </span>
  );
});

export interface EnumBadgeProps extends Omit<BadgeProps, 'tone' | 'children'> {
  display: EnumDisplay;
  /** Surface the enum's longer `description` as a native tooltip. */
  withTooltip?: boolean;
}

/**
 * Renders a domain enum through its shared display metadata, so
 * `SKIPPED_PRIOR_KNOWLEDGE` reads as "Skipped — prior knowledge" everywhere.
 */
export const EnumBadge = forwardRef<HTMLSpanElement, EnumBadgeProps>(function EnumBadge(
  { display, withTooltip = true, title, ...props },
  ref,
) {
  return (
    <Badge ref={ref} tone={display.tone} title={title ?? (withTooltip ? display.description : undefined)} {...props}>
      {display.label}
    </Badge>
  );
});

export { badgeVariants };
